import type { BrandRole, Capability, OrganizationRole } from "./roles.js";

/**
 * Wire types shared between `api` and `web`. Field names follow
 * docs/DATA_MODEL.md §2 verbatim, per the api-conventions skill
 * ("Follow DATA_MODEL.md entity/field names verbatim ... camelCase").
 */

/** ARCHITECTURE.md §5 / DATA_MODEL.md §2 — Brand.vectorTenancyMode. */
export type VectorTenancyMode = "pooled" | "dedicated";

/** DATA_MODEL.md §2 — Organization.ssoMode. */
export type SsoMode = "disabled" | "optional" | "enforced";

/**
 * DATA_MODEL.md §2 — Response.engine. Five engines at GA.
 *
 * Claude was dropped and Google AI Mode substituted (DECISIONS.md ENGINE-003):
 * Bright Data has no claude.ai scraper, and AI answers inside Google Search are
 * arguably more material to a CCO anyway — that is where journalists and the
 * general public actually land.
 */
export const ENGINES = [
  "chatgpt",
  "perplexity",
  "gemini",
  "copilot",
  "google_ai_mode",
] as const;
export type Engine = (typeof ENGINES)[number];

export const ENGINE_LABELS: Record<Engine, string> = {
  chatgpt: "ChatGPT",
  perplexity: "Perplexity",
  gemini: "Gemini",
  copilot: "Copilot",
  google_ai_mode: "Google AI Mode",
};

/** DATA_MODEL.md §2 — PersonaPromptTemplate.persona. PRD.md §8.2's library. */
export const PERSONAS = [
  "journalist",
  "investor",
  "regulator",
  "job_seeker",
  "b2b_buyer",
  "general_public",
] as const;
export type Persona = (typeof PERSONAS)[number];

export const PERSONA_LABELS: Record<Persona, string> = {
  journalist: "Journalist",
  investor: "Investor / Analyst",
  regulator: "Regulator",
  job_seeker: "Job Seeker",
  b2b_buyer: "B2B Buyer",
  general_public: "General Public",
};

/** How each persona actually asks — shapes generation, shown in the UI. */
export const PERSONA_INTENTS: Record<Persona, string> = {
  journalist:
    "Probing and sceptical. Looks for the gap between claim and evidence, and for anything newsworthy the company would rather not lead with.",
  investor:
    "Financially literate and forward-looking. Cares about materiality, guidance, risk disclosure, and whether numbers are verified.",
  regulator:
    "Compliance-focused and literal. Asks whether obligations are met, evidenced, and independently assured.",
  job_seeker:
    "Personal and culture-focused. Asks what it is actually like to work there, and whether stated values match reported experience.",
  b2b_buyer:
    "Evaluative and comparative. Cares about reliability, track record, and how the company stacks up against alternatives.",
  general_public:
    'Plain-language and reputational. Asks the broad "is this a good company / what are they known for" question.',
};

export interface OrganizationSummary {
  organizationId: string;
  name: string;
  slug: string;
  billingTier: string;
  ssoMode: SsoMode;
}

export interface BrandSummary {
  brandId: string;
  organizationId: string;
  brandName: string;
  vectorTenancyMode: VectorTenancyMode;
  enabledEngines: Engine[];
  crisisModeUntil: string | null;
  createdAt: string;
}

/** One resolved grant, as surfaced by GET /auth/me (drives FR-AUTH-05 landing). */
export interface BrandGrant {
  brandId: string;
  brandName: string;
  organizationId: string;
  role: BrandRole;
  /** True when this grant comes from OrganizationAdmin inheritance rather than a
   *  BrandMembership row (RBAC_MODEL.md §4). The UI labels these differently. */
  inherited: boolean;
  capabilities: Capability[];
}

export interface OrganizationGrant {
  organizationId: string;
  organizationName: string;
  role: OrganizationRole;
  capabilities: Capability[];
}

export interface CurrentUser {
  userId: string;
  email: string;
  name: string;
  isPlatformSuperAdmin: boolean;
  organizationGrants: OrganizationGrant[];
  brandGrants: BrandGrant[];
}

export interface InvitationPreview {
  organizationName: string;
  brandName: string | null;
  role: BrandRole | OrganizationRole;
  email: string;
  /** True when a User already exists for this email — the accept screen then
   *  asks only for confirmation, not for a name and password. */
  requiresAccountSetup: boolean;
}

export interface InvitationSummary {
  invitationId: string;
  email: string;
  organizationId: string;
  brandId: string | null;
  brandName: string | null;
  role: BrandRole | OrganizationRole;
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: string;
  createdAt: string;
}

export interface MembershipSummary {
  userId: string;
  email: string;
  name: string;
  role: BrandRole | OrganizationRole;
  inherited: boolean;
}

/** API_SPEC.md §1 — the error shape, used unchanged on every endpoint. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/** API_SPEC.md §1 — cursor pagination envelope. */
export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface SessionResponse extends AuthTokens {
  user: CurrentUser;
}
